import U2000CheckBox from '../../../../u2000Core/components/formControls/checkbox/U2000CheckBox';
import U2000InputText from '../../../../u2000Core/components/formControls/input/U2000InputText';
import type { U2000FormStateHandlers } from '../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { NS } from '../../../U3141';
import U2000InputCalendarCustom from '../../../../u2000Core/components/formControls/input/U2000InputCalendarCustom';
import { useTranslation } from 'react-i18next';
import U2000RadioGroupString from '../../../../u2000Core/components/formControls/radio/U2000RadioGroupString';
import { U2000FormStateEnumState } from '../../../../u2000Core/reducer/formReducer/U2000FormState';
import {
  anyLotDetailOperationTypeHasSupplementary,
  buildLotDetailOperationTypeRadioOptions,
  getOperationTypeLabelsWithSupplementaryInfoEnabled,
  isLotDetailSupplementaryEnabledForOperationType,
} from '../../common/U3141LotOperationType';
import { useU3141Context } from '../../../U3141Context';
import type { U3141Cadastre, U3141ChildLotChain, U3141District } from '../../../U3141Service';
import U2000InputTag, { U2000InputTagEnum } from '../../../../u2000Core/components/formControls/input/U2000InputTag';
import U2000AutoCompleteInput from '../../../../u2000Core/components/formControls/autoComplete/U2000AutoCompleteInput';
import { useRef, useState, useCallback, useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useU3141FindPropertyDistrictsHandlers, useU3141FindCadastresHandlers } from './U3141UseFindDistrictCadastreHandlers';
import { formatConcordanceEntryForDisplay } from './U3141ConcordanceUtils';
import { u3141FormatLotGraphDialogLotNumber } from '../../../tools/u3141FormatLotGraphLabel';
import U3141ConcordanceDialog from './U3141ConcordanceDialog';
import U2000Label from '../../../../u2000Core/components/formControls/label/U2000Label';
import U2000InvalidFeedback from '../../../../u2000Core/components/formControls/feedback/U2000InvalidFeedback';
import { FaPencilAlt } from 'react-icons/fa';

export type U3141ChildLotChainLotFieldsMode = 'form' | 'graphAdd';

interface U3141ChildLotChainLotFieldsProps {
  formStateHandlers: U2000FormStateHandlers<U3141ChildLotChain>;
  mode?: U3141ChildLotChainLotFieldsMode;
  /** Mode graphAdd : lecture seule des champs métier (cadenas dialogue BL-0695). Défaut true. */
  businessFieldsEditable?: boolean;
  /** Mode graphAdd : masquer le champ numéro de lot (géré par le dialogue). */
  hideLotNumberField?: boolean;
  /** Mode graphAdd : zone à droite du champ lot (ex. partie de lot, cadenas, changer de lot). */
  graphAddLotInlineEnd?: ReactNode;
  /** Mode graphAdd : pastille / badge à droite du libellé « Lot ». */
  graphAddLotLabelAddon?: ReactNode;
}

function U3141ChildLotChainLotFields({
  formStateHandlers,
  mode = 'form',
  businessFieldsEditable = true,
  hideLotNumberField = false,
  graphAddLotInlineEnd,
  graphAddLotLabelAddon,
}: U3141ChildLotChainLotFieldsProps) {
  const { t, i18n } = useTranslation();
  const { unitInitLU } = useU3141Context();
  const opTypesFromInit = unitInitLU.lotDetailOperationTypes;
  const supplementaryFieldTypeLabels = getOperationTypeLabelsWithSupplementaryInfoEnabled(opTypesFromInit, i18n?.language || 'fr');
  const [isConcordanceDialogOpen, setConcordanceDialogOpen] = useState(false);

  const rowData = formStateHandlers.getData() as U3141ChildLotChain | null | undefined;
  const selectedOperationType = rowData?.lotDetail?.operationType;
  const isGraphAdd = mode === 'graphAdd';
  const fieldsEditable = isGraphAdd
    ? businessFieldsEditable
    : formStateHandlers.getFormState() === U2000FormStateEnumState.EDIT;

  const formStateHandlersRef = useRef(formStateHandlers);
  formStateHandlersRef.current = formStateHandlers;

  useEffect(() => {
    const handlers = formStateHandlersRef.current;
    const row = handlers.getData() as U3141ChildLotChain | null | undefined;
    if (!row?.lotDetail || !opTypesFromInit?.length) return;
    if (!isLotDetailSupplementaryEnabledForOperationType(row.lotDetail.operationType, opTypesFromInit)) {
      const cur = row.lotDetail.operationTypeInfo;
      if (cur != null && String(cur).trim() !== '') {
        handlers.setData({
          ...row,
          lotDetail: { ...row.lotDetail, operationTypeInfo: null },
        });
      }
    }
  }, [selectedOperationType, opTypesFromInit]);

  const setCadastreSelection = useRef<((item: U3141Cadastre | null) => void) | null>(null);
  const setCadastreInputValue = useRef<((value: string) => void) | null>(null);

  const lotDetail = formStateHandlers.getData()?.lotDetail;
  const concordance = lotDetail?.concordance ?? [];
  const concordancePartieDeLot = lotDetail?.concordancePartieDeLot ?? [];
  const parentContext = useRef({
    codeDistrict: lotDetail?.district?.codeDistrict,
    cadastreCode: lotDetail?.cadastre?.cadastreCode,
  });
  parentContext.current = {
    codeDistrict: lotDetail?.district?.codeDistrict,
    cadastreCode: lotDetail?.cadastre?.cadastreCode,
  };

  const handleSaveConcordance = useCallback(
    (payload: { concordance: string[]; concordancePartieDeLot: boolean[] }) => {
      const current = formStateHandlers.getData();
      if (current?.lotDetail) {
        formStateHandlers.setData({
          ...current,
          lotDetail: {
            ...current.lotDetail,
            concordance: payload.concordance,
            concordancePartieDeLot: payload.concordancePartieDeLot,
          },
        });
      }
      setConcordanceDialogOpen(false);
    },
    [formStateHandlers],
  );

  const findPropertyDistrictsHandlers = useU3141FindPropertyDistrictsHandlers();
  const findCadastresHandlers = useU3141FindCadastresHandlers({
    getDistrictId: () => formStateHandlers.getData()?.lotDetail?.district?.id,
  });

  async function onSearchPropertyDistrict(criterias: { fieldName: string; value: unknown }[]) {
    return findPropertyDistrictsHandlers.search(criterias);
  }

  const displayPropertyDistrict = (item: unknown) => {
    const d = item as U3141District | null | undefined;
    if (d) {
      return `${d.propertyDistrict}`;
    }
    return '';
  };

  const extracDataForPropertyDistrict = (result: unknown) => {
    const r = result as U3141District | null | undefined;
    if (r) {
      return [
        { fieldName: 'lotDetail.district.id', value: r.id },
        { fieldName: 'lotDetail.district.propertyDistrict', value: r.propertyDistrict },
        { fieldName: 'lotDetail.district.codeDistrict', value: r.codeDistrict ?? null },
      ];
    }
    setCadastreSelection.current?.(null);
    setCadastreInputValue.current?.('');
    return [
      { fieldName: 'lotDetail.district.id', value: null },
      { fieldName: 'lotDetail.district.propertyDistrict', value: null },
      { fieldName: 'lotDetail.district.codeDistrict', value: null },
      { fieldName: 'lotDetail.cadastre.id', value: null },
      { fieldName: 'lotDetail.cadastre.cadastre', value: null },
      { fieldName: 'lotDetail.cadastre.cadastreCode', value: null },
      { fieldName: 'lotDetail.cadastre.rankCode', value: null },
      { fieldName: 'lotDetail.cadastre.rankLabel', value: null },
    ];
  };

  const onSearchCadastre = async (criterias: { fieldName: string; value: unknown }[]) => {
    if (!formStateHandlers.getData()?.lotDetail?.district?.id) return [];
    const result = await findCadastresHandlers.search(criterias);
    return {
      ...result,
      results: result.results.map((r: Record<string, unknown>) => ({ value: r.id, ...r })),
    };
  };

  const displayCadastre = (item: unknown) => {
    const c = item as Partial<U3141Cadastre> | null | undefined;
    if (c?.cadastre != null) {
      return `${c.cadastre} ${c.rankLabel ?? ''}`.trimEnd();
    }
    return '';
  };

  const extracDataForCadastre = (result: unknown) => {
    const r = result as U3141Cadastre | null | undefined;
    if (r) {
      return [
        { fieldName: 'lotDetail.cadastre.id', value: r.id },
        { fieldName: 'lotDetail.cadastre.cadastre', value: r.cadastre },
        { fieldName: 'lotDetail.cadastre.cadastreCode', value: r.cadastreCode ?? null },
        { fieldName: 'lotDetail.cadastre.rankCode', value: r.rankCode },
        { fieldName: 'lotDetail.cadastre.rankLabel', value: r.rankLabel },
      ];
    }
    return [
      { fieldName: 'lotDetail.cadastre.id', value: null },
      { fieldName: 'lotDetail.cadastre.cadastre', value: null },
      { fieldName: 'lotDetail.cadastre.cadastreCode', value: null },
      { fieldName: 'lotDetail.cadastre.rankCode', value: null },
      { fieldName: 'lotDetail.cadastre.rankLabel', value: null },
    ];
  };

  const lotReadonly = isGraphAdd || !fieldsEditable;

  return (
    <div className="flex flex-1 flex-col gap-2">
      {!hideLotNumberField ? (
        <div className="space-y-1.5">
          {isGraphAdd && graphAddLotLabelAddon != null ? (
            <div className="mb-1.5 flex min-h-[1.25rem] flex-wrap items-center gap-x-2 gap-y-1">
              <label
                htmlFor="lotDetail.lot"
                className="text-sm font-medium leading-5 text-gray-700 select-none"
              >
                {t('lblLotDetail.lot', { ns: NS })}
              </label>
              <span className="inline-flex items-center leading-none">{graphAddLotLabelAddon}</span>
            </div>
          ) : null}
          {isGraphAdd && lotReadonly ? (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-3">
              <div
                id="lotDetail.lot"
                className="flex min-h-[46px] min-w-0 flex-1 items-center rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-900"
                aria-readonly
              >
                {u3141FormatLotGraphDialogLotNumber(lotDetail?.lot)}
              </div>
              {graphAddLotInlineEnd != null ? (
                <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 sm:gap-3">{graphAddLotInlineEnd}</div>
              ) : null}
            </div>
          ) : (
          <U2000InputText
            formStateHandlers={formStateHandlers}
            fieldName="lotDetail.lot"
            label={t('lblLotDetail.lot', { ns: NS })}
            hideLabel={isGraphAdd && graphAddLotLabelAddon != null}
            readonly={lotReadonly}
            inlineEnd={
              isGraphAdd && graphAddLotInlineEnd != null ? (
                graphAddLotInlineEnd
              ) : (
                <U2000CheckBox
                  formStateHandlers={formStateHandlers}
                  fieldName="lotDetail.partieDeLot"
                  label={t('lblLotDetail.partieDeLot', { ns: NS })}
                  readonly={!fieldsEditable}
                />
              )
            }
          />
          )}
        </div>
      ) : isGraphAdd && graphAddLotInlineEnd != null ? (
        <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">{graphAddLotInlineEnd}</div>
      ) : (
        <U2000CheckBox
          formStateHandlers={formStateHandlers}
          fieldName="lotDetail.partieDeLot"
          label={t('lblLotDetail.partieDeLot', { ns: NS })}
          readonly={!fieldsEditable}
        />
      )}

      {isGraphAdd ? (
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2 md:items-start md:gap-x-4">
          <U2000AutoCompleteInput
            pageSize={15}
            formStateHandlers={formStateHandlers}
            fieldName="lotDetail.district"
            mainFieldName="propertyDistrict"
            label={t('lblLotDetail.propertyDistrict', { ns: NS })}
            onSearch={onSearchPropertyDistrict}
            displayFn={displayPropertyDistrict}
            overrideExtractDataFn={extracDataForPropertyDistrict}
            basicResultHeight={300}
            resultLabels={[]}
            resultFields={['propertyDistrict']}
            readonly={!fieldsEditable}
          />
          <U2000AutoCompleteInput
            pageSize={15}
            formStateHandlers={formStateHandlers}
            fieldName="lotDetail.cadastre"
            mainFieldName="cadastre"
            label={t('lblLotDetail.cadastre', { ns: NS })}
            onSearch={onSearchCadastre}
            displayFn={displayCadastre}
            overrideExtractDataFn={extracDataForCadastre}
            basicResultHeight={300}
            minCarLaunchSearch={3}
            resultLabels={[]}
            resultFields={['cadastre', 'rankLabel']}
            onSetCurrentSelection={(setCurrentSelection, setInputValue) => {
              setCadastreSelection.current = setCurrentSelection;
              setCadastreInputValue.current = setInputValue;
            }}
            readonly={!fieldsEditable || !formStateHandlers.getData()?.lotDetail?.district?.id}
          />
        </div>
      ) : (
        <>
          <U2000AutoCompleteInput
            pageSize={15}
            formStateHandlers={formStateHandlers}
            fieldName="lotDetail.district"
            mainFieldName="propertyDistrict"
            label={t('lblLotDetail.propertyDistrict', { ns: NS })}
            onSearch={onSearchPropertyDistrict}
            displayFn={displayPropertyDistrict}
            overrideExtractDataFn={extracDataForPropertyDistrict}
            basicResultHeight={300}
            resultLabels={[]}
            resultFields={['propertyDistrict']}
            readonly={!fieldsEditable}
          />
          <U2000AutoCompleteInput
            pageSize={15}
            formStateHandlers={formStateHandlers}
            fieldName="lotDetail.cadastre"
            mainFieldName="cadastre"
            label={t('lblLotDetail.cadastre', { ns: NS })}
            onSearch={onSearchCadastre}
            displayFn={displayCadastre}
            overrideExtractDataFn={extracDataForCadastre}
            basicResultHeight={300}
            minCarLaunchSearch={3}
            resultLabels={[]}
            resultFields={['cadastre', 'rankLabel']}
            onSetCurrentSelection={(setCurrentSelection, setInputValue) => {
              setCadastreSelection.current = setCurrentSelection;
              setCadastreInputValue.current = setInputValue;
            }}
            readonly={!fieldsEditable || !formStateHandlers.getData()?.lotDetail?.district?.id}
          />
        </>
      )}

      {isGraphAdd ? (
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2 md:items-start md:gap-x-4">
          <U2000InputCalendarCustom
            formStateHandlers={formStateHandlers}
            fieldName="lotDetail.dateEstablishment"
            label={t('lblLotDetail.dateEstablishment', { ns: NS })}
            readonly={!fieldsEditable}
          />
          <U2000InputTag
            formStateHandlers={formStateHandlers}
            fieldName="lotDetail.corrections"
            label={t('lblLotDetail.corrections', { ns: NS })}
            inputType={U2000InputTagEnum.DATE}
            readonly={!fieldsEditable}
          />
        </div>
      ) : (
        <U2000InputCalendarCustom
          formStateHandlers={formStateHandlers}
          fieldName="lotDetail.dateEstablishment"
          label={t('lblLotDetail.dateEstablishment', { ns: NS })}
          readonly={!fieldsEditable}
        />
      )}

      {(!isGraphAdd || (isGraphAdd && concordance.length > 0)) && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <U2000Label label={t('lblLotDetail.concordances', { ns: NS })} />
            {!isGraphAdd && formStateHandlers.getFormState() === U2000FormStateEnumState.EDIT && (
              <button
                type="button"
                onClick={() => setConcordanceDialogOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                aria-label={t('lblModifyConcordances', { ns: NS, defaultValue: 'Modifier les concordances' })}
              >
                <FaPencilAlt className="h-4 w-4" />
                <span>{t('lblModifyConcordances', { ns: NS, defaultValue: 'Modifier les concordances' })}</span>
              </button>
            )}
          </div>
          <div
            className={`flex min-h-[46px] w-full flex-wrap items-center gap-1.5 rounded-lg border border-gray-300 px-4 py-2 text-sm ${formStateHandlers.getFormState() === U2000FormStateEnumState.READ ? 'bg-muted' : 'bg-muted/50'}`}
          >
            {Array.isArray(concordance) && concordance.length > 0 ? (
              concordance.map((entry: string, idx: number) => {
                const label = formatConcordanceEntryForDisplay(entry, parentContext.current);
                const isReadOnly =
                  isGraphAdd || formStateHandlers.getFormState() === U2000FormStateEnumState.READ;
                const tagClasses = isReadOnly
                  ? 'inline-flex max-w-xs items-center rounded-full border border-gray-200 bg-muted px-2.5 py-1 text-xs font-medium text-gray-600 transition-all duration-150'
                  : 'inline-flex max-w-xs items-center rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 transition-all duration-150 hover:bg-blue-100';
                return (
                  <span key={`${idx}-${entry}`} className={tagClasses} title={label}>
                    {label}
                  </span>
                );
              })
            ) : (
              <span className="text-gray-500 italic">{t('lblConcordanceEmptyHint', { ns: NS, defaultValue: 'Aucune concordance' })}</span>
            )}
          </div>
          {typeof document !== 'undefined' &&
            createPortal(
              <U3141ConcordanceDialog
                isOpen={isConcordanceDialogOpen}
                onClose={() => setConcordanceDialogOpen(false)}
                concordance={concordance}
                concordancePartieDeLot={concordancePartieDeLot}
                onSave={handleSaveConcordance}
                parentContext={{
                  codeDistrict: lotDetail?.district?.codeDistrict,
                  cadastreCode: lotDetail?.cadastre?.cadastreCode,
                }}
              />,
              document.body,
            )}
          <U2000InvalidFeedback formStateHandlers={formStateHandlers} fieldName="lotDetail.concordance" />
        </div>
      )}

      {!isGraphAdd ? (
        <U2000InputTag
          formStateHandlers={formStateHandlers}
          fieldName="lotDetail.corrections"
          label={t('lblLotDetail.corrections', { ns: NS })}
          inputType={U2000InputTagEnum.DATE}
          readonly={!fieldsEditable}
        />
      ) : null}

      <div className="mt-3">
        <U2000RadioGroupString
          titleGroup={t('lblLotDetail.operationType', { ns: NS })}
          fieldName="lotDetail.operationType"
          orientation="horizontal"
          options={buildLotDetailOperationTypeRadioOptions(opTypesFromInit, i18n?.language || 'fr', selectedOperationType)}
          formStateHandlers={formStateHandlers}
          readonly={!fieldsEditable}
        >
          {anyLotDetailOperationTypeHasSupplementary(opTypesFromInit) ||
          isLotDetailSupplementaryEnabledForOperationType(selectedOperationType, opTypesFromInit) ? (
            <U2000InputText
              formStateHandlers={formStateHandlers}
              fieldName="lotDetail.operationTypeInfo"
              label={t('lblLotDetailSupplementaryInfo', { ns: NS, types: supplementaryFieldTypeLabels })}
              readonly={
                !fieldsEditable ||
                !isLotDetailSupplementaryEnabledForOperationType(selectedOperationType, opTypesFromInit)
              }
            />
          ) : null}
        </U2000RadioGroupString>
      </div>
    </div>
  );
}

export default U3141ChildLotChainLotFields;
